import React, { SyntheticEvent } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Edit, Plus, Trash2 } from 'lucide-react';

import { createVehicleIssue, deleteVehicleIssue, updateVehicleIssue } from 'actions/repairOrderActions';
import { displayConfirmationModal } from 'helpers/services/removeConfirmationService';
import VehicleIssue from 'models/VehicleIssue';

interface Props {
    vehicleIssues: VehicleIssue[];
    repairOrderId: string;
    dispatch: Dispatch;
}

interface State {
    editableIssueId: string | null;
    description: string | null;
    finding: string | null;
    isCreateNewVehicleIssueActive: boolean;
}

class CustomerConcerns extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            editableIssueId: null,
            description: '',
            finding: '',
            isCreateNewVehicleIssueActive: false,
        };

        this.handleChange = this.handleChange.bind(this);
        this.createNewIssue = this.createNewIssue.bind(this);
        this.updateIssue = this.updateIssue.bind(this);
    }

    createNewIssue() {
        const { description, finding } = this.state;
        const vehicleIssue = {
            description,
            finding,
        };

        const { dispatch } = this.props;
        dispatch<any>(createVehicleIssue(vehicleIssue as VehicleIssue, this.props.repairOrderId));
        this.setState({ description: null, finding: null, isCreateNewVehicleIssueActive: false });
    }

    updateIssue() {
        const { description, finding, editableIssueId } = this.state;
        const vehicleIssue = {
            description,
            finding,
            id: editableIssueId,
        };

        const { dispatch } = this.props;
        dispatch<any>(updateVehicleIssue(vehicleIssue as VehicleIssue));
        this.setState({ description: null, finding: null, editableIssueId: null });
    }

    handleChange(e: SyntheticEvent) {
        // @ts-ignore
        const { name, value } = e.target;
        // @ts-ignore
        this.setState({ [name]: value });
    }

    editVehicleIssue(vehicleIssue: VehicleIssue) {
        this.setState({
            editableIssueId: vehicleIssue.id,
            description: vehicleIssue.description,
            finding: vehicleIssue.finding,
            isCreateNewVehicleIssueActive: false,
        });
    }

    handleDelete(issue: VehicleIssue) {
        displayConfirmationModal(`customer's concern: "${issue.description}"`)
            .then(() => {
                this.props.dispatch<any>(deleteVehicleIssue(issue.id));
            })
            .catch((_) => _);
    }
    renderEditableForm(isEdit: boolean) {
        return (
            <div className="bg-white p-4 rounded-lg shadow border">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="text-sm font-semibold text-gray-700 mb-1 block">Customer Concern</label>
                        <textarea
                            name="description"
                            className="bg-gray-50 rounded-md border w-full px-3 py-2 focus:ring-1 focus:outline-none focus:border-blue-500"
                            onChange={this.handleChange}
                            value={this.state.description!}
                            rows={4}
                            autoFocus
                        ></textarea>
                    </div>
                    <div>
                        <label className="text-sm font-semibold text-gray-700 mb-1 block">Technician Comment</label>
                        <textarea
                            name="finding"
                            className="bg-gray-50 rounded-md border w-full px-3 py-2 focus:ring-1 focus:outline-none focus:border-blue-500"
                            onChange={this.handleChange}
                            value={this.state.finding!}
                            rows={4}
                        ></textarea>
                    </div>
                </div>
                <div className="mt-4 flex justify-end space-x-2">
                    <button
                        onClick={() =>
                            this.setState({
                                editableIssueId: null,
                                isCreateNewVehicleIssueActive: false,
                                description: null,
                                finding: null,
                            })
                        }
                        className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-200 font-semibold"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={isEdit ? this.updateIssue : this.createNewIssue}
                        className="px-4 py-2 bg-green-600 rounded-md text-white hover:bg-green-700 font-semibold"
                    >
                        {isEdit ? 'Update' : 'Save'}
                    </button>
                </div>
            </div>
        );
    }

    render() {
        const { vehicleIssues } = this.props;
        const { editableIssueId, isCreateNewVehicleIssueActive } = this.state;

        return (
            <div className="bg-gray-50 border rounded-lg overflow-hidden">
                <div className="flex justify-between items-center bg-gray-50 p-4 border-b">
                    <h2 className="font-semibold text-lg text-gray-800">Customer Concerns</h2>
                    {!isCreateNewVehicleIssueActive && !editableIssueId && (
                        <button
                            className="inline-flex items-center px-3 py-1.5 bg-white border border-gray-300 rounded-md text-green-600 hover:bg-gray-50 font-semibold text-sm"
                            onClick={() =>
                                this.setState({
                                    isCreateNewVehicleIssueActive: true,
                                    editableIssueId: null,
                                    description: '',
                                    finding: '',
                                })
                            }
                        >
                            <Plus size={16} className="mr-1" />
                            Add Concern
                        </button>
                    )}
                </div>

                <div className="divide-y">
                    {/* Editable Form */}
                    {isCreateNewVehicleIssueActive && !editableIssueId && this.renderEditableForm(false)}

                    {/* Concerns List */}
                    {vehicleIssues?.map((vehicleIssue, index) => {
                        return vehicleIssue.id === editableIssueId ? (
                            <div key={index} className="bg-gray-50 p-4">
                                {this.renderEditableForm(true)}
                            </div>
                        ) : (
                            <div key={index} className="bg-white p-4 hover:bg-gray-50">
                                <div className="flex justify-between items-start mb-2">
                                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <h3 className="text-sm font-semibold text-red-500 mb-1">
                                                Customer Concern
                                            </h3>
                                            <p className="text-gray-800 whitespace-pre-wrap">
                                                {vehicleIssue.description}
                                            </p>
                                        </div>
                                        <div>
                                            <h3 className="text-sm font-semibold text-blue-500 mb-1">
                                                Technician Comment
                                            </h3>
                                            <p className="text-gray-800 whitespace-pre-wrap">
                                                {vehicleIssue.finding || 'No comments yet'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex space-x-2 text-gray-500 ml-4">
                                        <button
                                            className="p-1 text-gray-500 hover:text-green-600"
                                            onClick={() => this.editVehicleIssue(vehicleIssue)}
                                            title="Edit"
                                        >
                                            <Edit size={16} />
                                        </button>

                                        <button
                                            className="p-1 text-gray-500 hover:text-red-600"
                                            title="Delete Group"
                                            onClick={() => this.handleDelete(vehicleIssue)}
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {vehicleIssues.length === 0 && !isCreateNewVehicleIssueActive && (
                        <div className="text-center py-8 bg-white">
                            <p className="text-gray-500">No customer concerns added yet</p>
                            <button
                                className="mt-2 inline-flex items-center px-4 py-2 bg-green-600 rounded-md text-white hover:bg-green-700 font-semibold"
                                onClick={() =>
                                    this.setState({
                                        isCreateNewVehicleIssueActive: true,
                                        editableIssueId: null,
                                        description: '',
                                        finding: '',
                                    })
                                }
                            >
                                <Plus size={16} className="mr-2" />
                                Add Customer Concern
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state: any) => ({
    vehicleIssues: state.repairOrder.vehicleIssues,
});

export default connect(mapStateToProps)(CustomerConcerns);
